/**
 * Единый словарь ошибок приложения.
 *
 * Каждая ошибка API возвращается в одном формате, чтобы фронтенд обрабатывал
 * их однообразно, а пользователь видел русский текст, а не stack trace.
 */

export const ERROR_CODES = {
  // 400
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_CURSOR: 'INVALID_CURSOR',
  INVALID_PHONE: 'INVALID_PHONE',
  BULK_LIMIT_EXCEEDED: 'BULK_LIMIT_EXCEEDED',
  IMPORT_PARSE_ERROR: 'IMPORT_PARSE_ERROR',
  READONLY_FIELD: 'READONLY_FIELD',
  CITY_COUNTRY_MISMATCH: 'CITY_COUNTRY_MISMATCH',

  // 401 / 403
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  FORBIDDEN: 'FORBIDDEN',
  ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
  PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',

  // 404
  CANDIDATE_NOT_FOUND: 'CANDIDATE_NOT_FOUND',
  DATE_GROUP_VISIBILITY_NOT_FOUND: 'DATE_GROUP_VISIBILITY_NOT_FOUND',
  FILE_NOT_FOUND: 'FILE_NOT_FOUND',
  DICTIONARY_ITEM_NOT_FOUND: 'DICTIONARY_ITEM_NOT_FOUND',
  VIEW_NOT_FOUND: 'VIEW_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  BLACKLIST_ENTRY_NOT_FOUND: 'BLACKLIST_ENTRY_NOT_FOUND',
  BLACKLISTED: 'BLACKLISTED',
  SHEET_NOT_FOUND: 'SHEET_NOT_FOUND',
  SHEET_ROW_NOT_FOUND: 'SHEET_ROW_NOT_FOUND',
  JOB_NOT_FOUND: 'JOB_NOT_FOUND',

  // 409
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  DUPLICATE_PHONE: 'DUPLICATE_PHONE',
  BASE_DUPLICATE: 'BASE_DUPLICATE',
  CANDIDATE_COMMENTS_FOUND: 'CANDIDATE_COMMENTS_FOUND',
  CANDIDATE_DELETED: 'CANDIDATE_DELETED',
  DICTIONARY_ITEM_IN_USE: 'DICTIONARY_ITEM_IN_USE',
  SHEET_CODE_TAKEN: 'SHEET_CODE_TAKEN',
  SHEET_COLUMN_KEY_TAKEN: 'SHEET_COLUMN_KEY_TAKEN',
  EMAIL_ALREADY_USED: 'EMAIL_ALREADY_USED',

  // 413 / 415
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',

  // 429
  RATE_LIMITED: 'RATE_LIMITED',

  // 500 / 503
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  STORAGE_UNAVAILABLE: 'STORAGE_UNAVAILABLE',
  QUEUE_UNAVAILABLE: 'QUEUE_UNAVAILABLE',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  VALIDATION_ERROR: 400,
  INVALID_CURSOR: 400,
  INVALID_PHONE: 400,
  BULK_LIMIT_EXCEEDED: 400,
  IMPORT_PARSE_ERROR: 400,
  READONLY_FIELD: 400,
  CITY_COUNTRY_MISMATCH: 400,

  UNAUTHENTICATED: 401,
  SESSION_EXPIRED: 401,
  FORBIDDEN: 403,
  ACCOUNT_DISABLED: 403,
  PASSWORD_CHANGE_REQUIRED: 403,

  CANDIDATE_NOT_FOUND: 404,
  DATE_GROUP_VISIBILITY_NOT_FOUND: 404,
  FILE_NOT_FOUND: 404,
  DICTIONARY_ITEM_NOT_FOUND: 404,
  VIEW_NOT_FOUND: 404,
  USER_NOT_FOUND: 404,
  BLACKLIST_ENTRY_NOT_FOUND: 404,
  BLACKLISTED: 409,
  SHEET_NOT_FOUND: 404,
  SHEET_ROW_NOT_FOUND: 404,
  JOB_NOT_FOUND: 404,

  VERSION_CONFLICT: 409,
  DUPLICATE_PHONE: 409,
  BASE_DUPLICATE: 409,
  CANDIDATE_COMMENTS_FOUND: 409,
  CANDIDATE_DELETED: 409,
  DICTIONARY_ITEM_IN_USE: 409,
  SHEET_CODE_TAKEN: 409,
  SHEET_COLUMN_KEY_TAKEN: 409,
  EMAIL_ALREADY_USED: 409,

  FILE_TOO_LARGE: 413,
  UNSUPPORTED_FILE_TYPE: 415,

  RATE_LIMITED: 429,

  INTERNAL_ERROR: 500,
  STORAGE_UNAVAILABLE: 503,
  QUEUE_UNAVAILABLE: 503,
};

/** Текст, который увидит пользователь. Русский, без технических деталей. */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_ERROR: 'Данные заполнены неверно',
  INVALID_CURSOR: 'Некорректная позиция прокрутки, обновите страницу',
  INVALID_PHONE: 'Не похоже на номер телефона',
  BULK_LIMIT_EXCEEDED: 'Слишком много строк за одну операцию',
  IMPORT_PARSE_ERROR: 'Не удалось разобрать файл',
  READONLY_FIELD: 'Это поле нельзя изменить вручную',
  CITY_COUNTRY_MISMATCH: 'Город не относится к выбранному гражданству',

  UNAUTHENTICATED: 'Требуется вход в систему',
  SESSION_EXPIRED: 'Сессия истекла, войдите заново',
  FORBIDDEN: 'Недостаточно прав для этого действия',
  ACCOUNT_DISABLED: 'Учётная запись заблокирована',
  PASSWORD_CHANGE_REQUIRED: 'Смените временный пароль, чтобы продолжить',

  CANDIDATE_NOT_FOUND: 'Кандидат не найден',
  DATE_GROUP_VISIBILITY_NOT_FOUND: 'Правило скрытия дат не найдено',
  FILE_NOT_FOUND: 'Файл не найден',
  DICTIONARY_ITEM_NOT_FOUND: 'Значение справочника не найдено',
  VIEW_NOT_FOUND: 'Представление не найдено',
  USER_NOT_FOUND: 'Пользователь не найден',
  BLACKLIST_ENTRY_NOT_FOUND: 'Запись чёрного списка не найдена',
  BLACKLISTED: 'Человек в чёрном списке',
  SHEET_NOT_FOUND: 'Лист не найден или закрыт для вашей роли',
  SHEET_ROW_NOT_FOUND: 'Строка листа не найдена',
  JOB_NOT_FOUND: 'Задача не найдена',

  VERSION_CONFLICT: 'Эту строку уже изменил другой пользователь',
  DUPLICATE_PHONE: 'Кандидат с таким телефоном уже есть в базе',
  BASE_DUPLICATE: 'В Базе уже есть кандидат с таким ФИО или телефоном',
  CANDIDATE_COMMENTS_FOUND: 'Найдены комментарии в прежних заявках кандидата',
  CANDIDATE_DELETED: 'Кандидат удалён другим пользователем',
  DICTIONARY_ITEM_IN_USE: 'Значение используется и не может быть удалено',
  SHEET_CODE_TAKEN: 'Лист с таким ключом уже есть',
  SHEET_COLUMN_KEY_TAKEN: 'Колонка с таким ключом на листе уже есть',
  EMAIL_ALREADY_USED: 'Пользователь с таким e-mail уже существует',

  FILE_TOO_LARGE: 'Файл слишком большой',
  UNSUPPORTED_FILE_TYPE: 'Такой тип файла загружать нельзя',

  RATE_LIMITED: 'Слишком много запросов, повторите чуть позже',

  INTERNAL_ERROR: 'Внутренняя ошибка сервера',
  STORAGE_UNAVAILABLE: 'Хранилище файлов недоступно',
  QUEUE_UNAVAILABLE: 'Сервис фоновых задач недоступен',
};

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    /** Ошибки по конкретным полям формы: { birthDate: 'Дата в будущем' } */
    fields?: Record<string, string>;
    /** Произвольные детали: текущая версия строки при 409, id дубля и т.п. */
    details?: Record<string, unknown>;
    requestId?: string;
  };
}

/**
 * Ошибка приложения. Бросается в сервисном слое, ловится единым
 * обработчиком Hono и превращается в ApiErrorBody.
 */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: ErrorCode,
    options: {
      message?: string;
      fields?: Record<string, string>;
      details?: Record<string, unknown>;
      cause?: unknown;
    } = {},
  ) {
    super(options.message ?? ERROR_MESSAGES[code], { cause: options.cause });
    this.name = 'AppError';
    this.code = code;
    this.status = ERROR_HTTP_STATUS[code];
    this.fields = options.fields;
    this.details = options.details;
  }

  toBody(requestId?: string): ApiErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.fields ? { fields: this.fields } : {}),
        ...(this.details ? { details: this.details } : {}),
        ...(requestId ? { requestId } : {}),
      },
    };
  }
}

export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError;
}
